// Floating top-left brand corner + top-right profile/theme corner.
// Used on every non-project-room page so the chrome is consistent —
// no opaque top bar, just two floating clusters in the corners.

import { useNavigate } from "react-router-dom";
import { BrandLogo } from "./Brand";
import { UserPopout } from "./UserPopout";
import { ThemeIcon } from "../theme/ThemeIcon";

declare const __APP_VERSION__: string;

function envLabel(): "staging" | "production" | "local" {
  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
  if (apiUrl.includes("staging")) return "staging";
  if (apiUrl.includes("waytide.dev")) return "production";
  return "local";
}

/// Column width matches the project-room rails so the corner chrome
/// sits at the exact same screen position across every page.
const RAIL_WIDTH_PX = 160;

export function BrandCorner() {
  const navigate = useNavigate();
  const env = envLabel();
  const appVersion = typeof __APP_VERSION__ === "undefined" ? "0.1.0" : __APP_VERSION__;
  // Bottom-left brand cluster — Waytide brand link + version status
  // line, centred horizontally in the rail. 2026-04-28: tightened
  // vertical rhythm. Single flex-col with one shared `py` so the
  // two children get equal breathing room from the border and from
  // each other; previously had per-button padding that left the
  // version text orphaned in a too-large band.
  return (
    <div
      className="pointer-events-none fixed bottom-0 left-0 z-40 flex flex-col items-center gap-1 border-t border-edge py-2"
      style={{ width: RAIL_WIDTH_PX }}
      data-testid="chrome-brand-corner"
    >
      <button
        onClick={() => navigate("/")}
        aria-label="Home"
        title="Home"
        className="pointer-events-auto flex cursor-pointer items-center gap-1.5 text-accent transition-colors hover:text-primary"
        data-testid="chrome-brand-home"
      >
        <BrandLogo size={14} />
        <span className="text-xs font-semibold leading-none text-primary">
          Waytide
        </span>
      </button>
      <button
        onClick={() => navigate("/status")}
        title="Status"
        className="pointer-events-auto cursor-pointer text-[0.625rem] leading-none text-faint transition-colors hover:text-primary"
        data-testid="chrome-brand-version"
      >
        v{appVersion}
        {env !== "production" ? ` · ${env}` : ""}
      </button>
    </div>
  );
}

export function UserCorner() {
  // Chrome symmetry pass: icons sit on the same baseline as the tab
  // labels on the left of the tab bar — same bottom-align (pb-2),
  // same gap-4, same horizontal padding (pr-5 mirrors the tab bar's
  // left px-5). Icons render as bare glyphs (no rounded box, no fill)
  // so they match the tab buttons' text-only visual weight.
  return (
    <div
      className="pointer-events-none fixed right-0 top-0 z-40 flex h-14 items-end justify-end gap-4 pr-5 pb-2"
      style={{ width: RAIL_WIDTH_PX }}
      data-testid="chrome-user-corner"
    >
      <div className="pointer-events-auto">
        <ThemeIcon />
      </div>
      <div className="pointer-events-auto">
        <UserPopout />
      </div>
    </div>
  );
}

/// Drop both corners into a page. Use inside a page's outermost
/// fragment, then render the page's main content beneath.
export function ChromeCorners() {
  return (
    <>
      <BrandCorner />
      <UserCorner />
    </>
  );
}
